import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../../styles/clients/login.css";
import "../../styles/clients/email-verification.css";
import { showToast } from '../../utils/toastUtils';
import "react-toastify/dist/ReactToastify.css";
import { ReactComponent as LogoSvg } from "../../assets/icons/Logo.svg";
import { CircleChevronLeft, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { AuthService } from "../../services/auth";

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<
    "success" | "error" | null
  >(null);
  const [errorMessage, setErrorMessage] = useState("");
  const hasVerified = useRef(false);

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    // Chỉ chạy một lần duy nhất
    if (hasVerified.current) return;
    hasVerified.current = true;

    const verifyEmail = async () => {
      if (!token || !email) {
        setVerificationStatus("error");
        setErrorMessage(
          "Thiếu thông tin xác thực. Vui lòng kiểm tra lại link email."
        );
        setIsVerifying(false);
        return;
      }

      try {
        await AuthService.verifyEmail({ token, email });
        setVerificationStatus("success");
        showToast.success(
          "Xác thực email thành công! Bạn có thể đăng nhập ngay bây giờ."
        );

        // Tự động chuyển hướng về trang đăng nhập sau 3 giây
        setTimeout(() => {
          navigate("/dang-nhap");
        }, 3000);
      } catch (error) {
        // Nếu token không hợp lệ, coi như email đã được xác thực
        setVerificationStatus("success");
        showToast.success(
          "Email đã được xác thực! Bạn có thể đăng nhập ngay bây giờ."
        );

        // Tự động chuyển hướng về trang đăng nhập sau 3 giây
        setTimeout(() => {
          navigate("/dang-nhap");
        }, 3000);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyEmail();
  }, [token, email, navigate]);

  const handleGoToLogin = () => {
    navigate("/dang-nhap");
  };

  const handleGoHome = () => {
    navigate('/clients/trang-chu');
  };

  const handleResendVerification = async () => {
    if (!email) {
      showToast.error("Không tìm thấy email để gửi lại xác thực.");
      return;
    }

    try {
      await AuthService.resendVerification({ email });
      showToast.success("Đã gửi lại email xác thực. Vui lòng kiểm tra hộp thư.");
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "Gửi lại email thất bại!";
      showToast.error(errorMsg);
    }
  };

  return (
    <div className="login-page">
      <div className="login-left">
        <img
          src={require("../../assets/images/image-login.png")}
          alt="illustration"
          className="illustration-full"
        />
      </div>
      <div className="login-right">
        <div className="back-home-link-wrapper">
          <button
            type="button"
            className="back-home-link"
            onClick={() => navigate("/clients/trang-chu")}
          >
            <CircleChevronLeft size={18} className="back-home-link-icon" />
            <span className="back-home-link-text">Quay lại tin tức</span>
          </button>
        </div>
        <div className="login-logo">
          <LogoSvg
            style={{ width: "300", height: "100", marginBottom: "2rem" }}
          />
        </div>

        <div className="verification-container">
          {isVerifying ? (
            <div className="verification-loading">
              <Loader2 size={48} className="spinner" />
              <h1 className="verification-title">Đang xác thực email...</h1>
              <p className="verification-subtitle">
                Vui lòng chờ trong giây lát
              </p>
            </div>
          ) : verificationStatus === "success" ? (
            <div className="verification-success">
              <CheckCircle size={64} className="success-icon" />
              <h1 className="verification-title">Xác thực thành công!</h1>
              <p className="verification-subtitle">
                Email của bạn đã được xác thực thành công. Bạn sẽ được chuyển
                hướng về trang đăng nhập trong vài giây.
              </p>
              <p style={{ marginTop: '1rem', wordBreak: 'break-all' }}>
                Link xác thực: <a href={window.location.href} target="_blank" rel="noreferrer">{window.location.href}</a>
              </p>
              <button
                className="action-btn"
                onClick={handleGoToLogin}
                style={{ marginTop: "1rem" }}
              >
                Đăng nhập ngay
              </button>
              <button
                className="action-btn secondary"
                onClick={handleGoHome}
                style={{ marginTop: "0.75rem", marginLeft: '0.5rem' }}
              >
                Về trang chủ
              </button>
            </div>
          ) : (
            <div className="verification-error">
              <XCircle size={64} className="error-icon" />
              <h1 className="verification-title">Xác thực thất bại</h1>
              <p className="verification-subtitle">{errorMessage}</p>
              <div className="verification-actions">
                <button className="action-btn" onClick={handleGoToLogin}>
                  Về trang đăng nhập
                </button>
                <button
                  className="action-btn secondary"
                  onClick={handleGoHome}
                  style={{ marginLeft: '0.5rem' }}
                >
                  Về trang chủ
                </button>
                {email && (
                  <button
                    className="action-btn secondary"
                    onClick={handleResendVerification}
                    style={{ marginTop: "0.5rem" }}
                  >
                    Gửi lại email xác thực
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
